/**
 * A3 Prüfstand des Anfo-Werts (Workshop-Lücken 29.10.2026, Plan `2026-10-29-workshop-luecken.md` § Paket M). Stand
 * 29.10.: der Abgleich „nachgerechnet zum Monatsersten" ist NICHT abgenommen (Begründung im Kopf von
 * `node-app/src/services/auswertungen/pruefstandService.ts`) — die Karte zeigt deshalb keine Zahl, nur wann der echte
 * Prüfstand mit gespeicherten Vorhersagen möglich ist. Quelle: `/umsatz/anfo-wert/pruefstand`.
 */
import { FC } from 'react';
import { Box } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import { Hinweis, Laden, datum, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { TERMS } from '../../../helpers/kpiDefinitions';
import { fetchPruefstand, PruefstandAntwort } from '../../../helpers/umsatzData';

const TERMIN: Record<PruefstandAntwort['termine'][number]['key'], string> = {
  tag75: 'Zahlung bis Tag 75',
  monate6: 'Zahlung bis 6 Monate',
  monate12: 'Zahlung bis 12 Monate',
};

const PruefstandKarte: FC = () => {
  const { data, fehler } = useLaden(fetchPruefstand);
  return (
    <ChartCard
      title="Stimmt der Anfo-Wert? Prüfstand"
      howToRead="Hier steht künftig, was das Modell für die Anfos eines Bestellmonats vorhergesagt hat und was sie bis zum selben Alter wirklich gezahlt haben. Belastbar ist das nur mit Vorhersagen, die damals gespeichert wurden. Die gibt es seit dem ersten Lauf des heutigen Modells; die Termine unten sagen, ab wann die erste Prüfung möglich ist."
      terms={[{ label: TERMS.pruefstand.label, tip: TERMS.pruefstand.tip }]}
      source="Läufe des Anfo-Werts (gespeicherte Vorhersagen), PL-X-Zahlungsstände je Tag seit 15.05.2026"
      freshnessKey={['anfo_werte', 'plx_orders']}
    >
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          <Box sx={{ fontSize: 14, lineHeight: 1.6, maxWidth: '75ch', mb: 1.5 }}>
            Noch keine Prüfstand-Zahl. Ein Nachrechnen mit heutigen Daten verzerrt die Vorhersage: Das Modell sähe für
            frühere Monate schon die späteren Zahlungen und stufte manche Quartalskunden als Jahreskunden ein. Gemessen
            am 29.10. hob das die Zahlungen, auf die das Modell rechnet, je nach Monat um 7 bis 24 % an, also genau in der
            Größe der Abweichung, die geprüft werden soll.
          </Box>
          {data.termine.length === 0 ? (
            <Hinweis>Noch kein Lauf des Modells mit Wert je Angebot.</Hinweis>
          ) : (
            <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 14, lineHeight: 1.8 }}>
              {data.termine.map((t) => (
                <li key={t.key}>
                  {TERMIN[t.key]}: ab <strong>{datum(t.ab)}</strong>
                  {t.erreicht ? ' (erreicht)' : ''}
                </li>
              ))}
            </Box>
          )}
          <Hinweis>
            Gezählt ab dem ersten Lauf am {data.erster_m5_lauf ? datum(data.erster_m5_lauf) : '–'}: Bis zu den Terminen erreichen die ersten
            Anfos mit gespeicherter Vorhersage das jeweilige Alter.
          </Hinweis>
        </>
      )}
    </ChartCard>
  );
};

export default PruefstandKarte;
